import {
  takeLatest,
  select,
  fork,
  call,
  put,
} from 'redux-saga/effects';
import {
  CHAT_SET_ACTIVITY_SUCCESS,
  CHAT_GET_MESSAGES_REQUEST,
  CHAT_GET_MESSAGES_SUCCESS,
  CHAT_SEND_MESSAGE_REQUEST,
  CHAT_UPDATE_MESSAGE_REQUEST,
  CHAT_DELETE_MESSAGE_REQUEST,
} from '@/action-types/chat';
import {
  getMessages,
  sendMessage,
  updateMessage,
  deleteMessage,
} from '@/api/chat';
import { ERROR_UPDATE_REQUEST, ERROR_PASS_REQUEST } from '@/action-types/error';
import utils from '@/utils/auth';

export function* setLastActivityRequest(params: {
  lastActivity: {
    action: string;
    id?: number;
    from?: string;
    state: 'in-progress' | 'success' | 'error';
    text?: string;
  };
}) {
  const state = yield select();

  yield put({
    type: CHAT_SET_ACTIVITY_SUCCESS,
    data: {
      lastActivity: {
        ...(state.book?.chat?.lastActivity || {}),
        ...(params.lastActivity || {}),
      },
    },
  });
}

export function* getMessagesRequest(params: {
  force: boolean;
}) {
  const state = yield select();
  const data = { messages: state.book.chat?.messages || [] };

  try {
    if (!data.messages || !data.messages.length || params.force) {
      const response: any = yield call(getMessages, state.book.route.sessionId);
      data.messages = response.data?.getMessages;
    }

    yield put({
      type: CHAT_GET_MESSAGES_SUCCESS,
      data,
    });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'getMessagesRequest'] });
    yield put({ type: ERROR_UPDATE_REQUEST, errorData: e });
  }
}

export function* sendMessageRequest(params: {
  data: {
    message: string;
  };
}) {
  const state = yield select();
  const userData = yield call(utils.parseToken, utils.getAccessToken());
  const teacher = userData.role === 'teacher' ? `${state.book.sync.teacher.id}` : null;
  const activityData = {
    lastActivity: {
      action: 'send',
      from: userData.role,
      state: 'in-progress',
      text: params.data.message,
    },
  };

  yield call(setLastActivityRequest, {
    lastActivity: {
      action: 'send',
      from: userData.role,
      state: 'in-progress',
      text: params.data.message,
    },
  });

  try {
    const sendResponse = yield call(sendMessage, {
      session: state.book.route.sessionId,
      lesson: state.book.lesson.currentId,
      message: params.data.message,
      teacher,
    });

    yield call(setLastActivityRequest, {
      lastActivity: {
        ...activityData.lastActivity,
        id: sendResponse.data?.sendMessage?.id,
        state: 'success',
      },
    });
    yield fork(getMessagesRequest, { ...params, force: true });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'sendMessageRequest'] });
    yield put({ type: ERROR_UPDATE_REQUEST, errorData: e });
    yield call(setLastActivityRequest, {
      lastActivity: {
        ...activityData.lastActivity,
        state: 'error',
      },
    });
  }
}

export function* updateMessageRequest(params: {
  data: {
    id: number;
    message: string;
  };
}) {
  yield call(setLastActivityRequest, {
    lastActivity: {
      action: 'update',
      id: params.data.id,
      state: 'in-progress',
      text: params.data.message,
    },
  });

  try {
    yield call(updateMessage, params.data);
    yield call(setLastActivityRequest, {
      lastActivity: {
        action: 'update',
        id: params.data.id,
        state: 'success',
        text: params.data.message,
      },
    });
    yield fork(getMessagesRequest, { ...params, force: true });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'updateMessageRequest'] });
    yield put({ type: ERROR_UPDATE_REQUEST, errorData: e });
    yield call(setLastActivityRequest, {
      lastActivity: {
        action: 'update',
        id: params.data.id,
        state: 'error',
        text: params.data.message,
      },
    });
  }
}

export function* deleteMessageRequest(params: {
  data: {
    id: number;
  };
}) {
  yield call(setLastActivityRequest, {
    lastActivity: {
      action: 'delete',
      id: params.data.id,
      state: 'in-progress',
    },
  });

  try {
    yield call(deleteMessage, params.data.id);
    yield call(setLastActivityRequest, {
      lastActivity: {
        action: 'delete',
        id: params.data.id,
        state: 'success',
      },
    });
    yield fork(getMessagesRequest, { ...params, force: true });
  } catch (e) {
    yield put({ type: ERROR_PASS_REQUEST, error: e, tags: ['logic', 'deleteMessageRequest'] });
    yield put({ type: ERROR_UPDATE_REQUEST, errorData: e });
    yield call(setLastActivityRequest, {
      lastActivity: {
        action: 'delete',
        id: params.data.id,
        state: 'error',
      },
    });
  }
}

export default function* grammarSaga() {
  yield takeLatest<{ type: string; } & any>(CHAT_GET_MESSAGES_REQUEST, getMessagesRequest);
  yield takeLatest<{ type: string; } & any>(CHAT_SEND_MESSAGE_REQUEST, sendMessageRequest);
  yield takeLatest<{ type: string; } & any>(CHAT_UPDATE_MESSAGE_REQUEST, updateMessageRequest);
  yield takeLatest<{ type: string; } & any>(CHAT_DELETE_MESSAGE_REQUEST, deleteMessageRequest);
}
